import { createElement, type ReactNode, useCallback, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { queryCoverageRuns } from '@/features/codeCoverage/data/coverageRuns';
import { emitCoverageRefresh } from '@/features/codeCoverage/state/coverageRefreshSignal';
import { getUploadById } from '@/features/uploads/data';
import { consumeUploadToastIntent } from '@/shared/notifications/uploadToastIntent';
import { Inline, Link, Text } from '@/ui';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';

type UseCoverageUploadPollingParams = {
  selectedOrganisationId: string | null;
  invalidateCoverageData: () => Promise<void>;
  notify: (args: { message: ReactNode; tone: 'blue' | 'green' | 'orange' | 'red' }) => void;
};

const buildUploadToastMessage = ({ label, href }: { label: string; href: string }): ReactNode =>
  createElement(
    Inline,
    { gap: 'sm', align: 'center' },
    createElement(Text, { role: 'body', component: 'span', children: label }),
    createElement(Link, {
      component: RouterLink,
      to: href,
      underline: 'always',
      color: 'inherit',
      children: 'View',
    }),
  );

export const useCoverageUploadPolling = ({
  selectedOrganisationId,
  invalidateCoverageData,
  notify,
}: UseCoverageUploadPollingParams): ((uploadId: string) => void) => {
  const uploadPollRef = useRef<Set<string>>(new Set());
  const uploadPollTimeoutRef = useRef<Partial<Record<string, ReturnType<typeof setTimeout>>>>({});
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      Object.values(uploadPollTimeoutRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      uploadPollTimeoutRef.current = {};
      uploadPollRef.current.clear();
    };
  }, []);

  return useCallback(
    (uploadId: string) => {
      if (uploadPollRef.current.has(uploadId)) return;
      uploadPollRef.current.add(uploadId);

      const maxAttempts = 20;
      const delayMs = 3000;
      let attempt = 0;

      const stopPolling = (): void => {
        const timeoutId = uploadPollTimeoutRef.current[uploadId];
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
          uploadPollTimeoutRef.current[uploadId] = undefined;
        }
        uploadPollRef.current.delete(uploadId);
      };

      const run = async (): Promise<void> => {
        if (!isMountedRef.current) {
          stopPolling();
          return;
        }

        attempt += 1;

        try {
          const upload = await getUploadById(uploadId, selectedOrganisationId ?? undefined);
          const fileLabel = upload.fileName || 'coverage';

          const showFallbackToast = (): void => {
            if (!consumeUploadToastIntent(uploadId)) {
              return;
            }

            if (upload.status === 'completed') {
              notify({
                message: buildUploadToastMessage({
                  label: `${fileLabel} uploaded`,
                  href: `/uploads/${uploadId}`,
                }),
                tone: 'green',
              });
              return;
            }

            const errorSuffix = upload.error ? ` - ${upload.error}` : '';
            const message =
              upload.status === 'completed_with_errors'
                ? `Upload completed with errors: ${fileLabel}${errorSuffix}.`
                : `Upload failed: ${fileLabel}${errorSuffix}.`;

            notify({
              message: buildUploadToastMessage({
                label: message,
                href: `/uploads/${uploadId}`,
              }),
              tone: upload.status === 'completed_with_errors' ? 'orange' : 'red',
            });
          };

          const isTerminal =
            upload.status === 'completed' ||
            upload.status === 'completed_with_errors' ||
            upload.status === 'failed';

          if (upload.status === 'completed') {
            const coverageRuns = await queryCoverageRuns(
              {
                page: 1,
                pageSize: 1,
                filters: buildEqFilter('uploadId', uploadId),
              },
              selectedOrganisationId ?? undefined,
            );

            if (coverageRuns.items.length > 0) {
              await invalidateCoverageData();
              emitCoverageRefresh();
              showFallbackToast();
              stopPolling();
              return;
            }
          } else if (isTerminal) {
            await invalidateCoverageData();
            emitCoverageRefresh();
            showFallbackToast();
            stopPolling();
            return;
          }
        } catch {
          // Keep polling while transient lookup failures settle.
        }

        if (attempt < maxAttempts) {
          uploadPollTimeoutRef.current[uploadId] = setTimeout(() => {
            void run();
          }, delayMs);
          return;
        }

        stopPolling();
      };

      void run();
    },
    [invalidateCoverageData, notify, selectedOrganisationId],
  );
};
